import * as React from 'react';
import { useOfferDetailsContext } from '@frontend/context/OfferDetailsContext';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/shadcn/components/ui/sheet';
import { Button } from '@/shadcn/components/ui/button';
import { Checkbox } from '@/shadcn/components/ui/checkbox';
import { Label } from '@/shadcn/components/ui/label';
import { Input } from '@/shadcn/components/ui/input';
import { IPayoutVariant } from '../OfferForm';

interface RefreshMemberTableFilterProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedPayoutIds: number[];
    setSelectedPayoutIds: (ids: number[]) => void;
}

export function RefreshMemberTableFilter({
    open,
    onOpenChange,
    selectedPayoutIds,
    setSelectedPayoutIds,
}: RefreshMemberTableFilterProps) {
    const {
        offer,
    } = useOfferDetailsContext() || {};
    const [searchText, setSearchText] = React.useState('');
    React.useEffect(() => {
        setSelectedPayoutIds(offer?.payouts?.map((payout) => payout.id) || []);
    }, [offer, setSelectedPayoutIds]);
    function handleApply() {
        onOpenChange(false);
    }

    function handleCancel() {
        onOpenChange(false);
    }

    function handleToggle(id: number) {
        setSelectedPayoutIds(
            selectedPayoutIds.includes(id)
                ? selectedPayoutIds.filter((pid) => pid !== id)
                : [...selectedPayoutIds, id]
        );
    }

    function handleDeselectAll() {
        setSelectedPayoutIds([]);
    }

    const filteredPayouts = React.useMemo(() => {
        if (!searchText) return offer?.payouts || [];
            return (offer?.payouts || []).filter((payout: IPayoutVariant) =>
            payout.label.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [offer?.payouts, searchText]);

    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="flex flex-col h-full bg-background p-0 gap-0">
          <SheetHeader className="p-4">
            <SheetTitle className="m-0">Filters</SheetTitle>
          </SheetHeader>
          <div className="border-b" />
          <div className="flex-1 px-4 flex flex-col gap-6 py-4">
            <div className="flex flex-col gap-2">
              {/* Payout Filter */}
              <Label>Last Payouts</Label>
              <div className="mb-2 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
                <Input
                  type="search"
                  placeholder="Search"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="rounded-lg bg-background pl-8 w-full focus:ring-0 focus-visible:ring-0 outline-none"
                />

              </div>
              <div>
                <div className="mb-2 text-muted-foreground cursor-pointer underline text-sm" onClick={handleDeselectAll}>Deselect All</div>
                {filteredPayouts.map((payout: IPayoutVariant) => (
                  <div key={payout.id} className="flex items-center gap-2 mb-2">
                    <Checkbox
                      checked={selectedPayoutIds.includes(payout.id)}
                      onCheckedChange={() => handleToggle(payout.id)}
                      id={`payout-${payout.id}`}
                    />
                    <Label className="text-sm font-normal text-primary" htmlFor={`payout-${payout.id}`}>
                      {payout.label}
                      {payout.flatPayout && payout.percentPayout
                                            ? ` ($${payout.flatPayout} + ${payout.percentPayout}%)`
                                            : payout.flatPayout
                                                ? ` ($${payout.flatPayout})`
                                                : payout.percentPayout
                                                    ? ` (${payout.percentPayout}%)`
                                                    : ''}
                    </Label>
                  </div>
                            ))}
              </div>
            </div>
            {/* <div>
                        Boost filter
                        <Label>Boost</Label>

                    </div> */}
          </div>
          <div className="border-t" />
          <SheetFooter className="justify-start sm:justify-start p-4">
            <Button onClick={handleApply}>Apply</Button>
            <Button variant="outline" onClick={handleCancel}>Cancel</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    );
}
